import { useCallback } from 'react';
import { dispatcher } from 'dispatcher';
import { FORM_ERROR } from 'final-form';
import { path } from 'ramda';

import { storageService } from '@core/js/services/storage.service';

import { RECAPTCHA_ERROR_CODE } from '@interlayer/js/app/components/main/ReCaptchaWithTitle/constants';
import { RETAIL_PLATFORM } from '@interlayer/js/app/components/main/Registration/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { getSha256 } from '@interlayer/js/app/utils/getSha256';
import { useNavigate } from '@interlayer/js/hooks/navigate';

import { showLoginExtendedErrorPopup } from 'js/app/components/main/Login/actions';
import {
    ACCOUNT_FROZEN,
    ACCOUNT_FROZEN_ERROR_CODE,
    ACCOUNT_FROZEN_SECOND,
    DEFAULT_ACCOUNT_FROZEN_ARTICLE_AFTER_LOGIN,
    DEFAULT_ACCOUNT_FROZEN_ARTICLE_AFTER_REGISTRATION,
    DEFAULT_RETAIL_SELF_EXCLUSION_LOGIN_ARTICLE,
    DEFAULT_SELF_EXCLUSION_LOGIN_ARTICLE,
    DEFAULT_TIME_OUT_LOGIN_ARTICLE,
    DOMINANT_CARD_ERROR_CODE,
    FAILED_LOGIN_ATTEMPTS,
    INCORRECT_USERNAME_OR_PASSWORD_ERROR_CODE,
    ONLY_ARTICLES_CONTENT,
    SELF_EXCLUSION_LOGIN_CODE,
    TIME_OUT_LOGIN_CODE,
    WITH_LOGO_POPUPS } from 'js/app/components/main/Login/constants';
import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { useCloseLoginPopup } from 'js/components/popups/LoginPopup/buzz/hooks/useCloseLoginPopup';
import { storeRememberMeItem } from 'js/hooks/auth/helpers';
import { useTranslations } from 'js/hooks/i18n';
import { useShowManagedPopup } from 'js/hooks/useShowManagedPopup';
import { htcmdService } from 'js/services/htcmd.service';

export const useExtendedErrorHandling = () => {
    const navigate = useNavigate();
    const translate = useTranslations('playtech.mobile.system.login.');
    const closeLoginPopup = useCloseLoginPopup();
    const loginExtendedErrorPopup = useShowManagedPopup(showLoginExtendedErrorPopup);
    const { config: { customClientPlatform: regClientPlatform } } = useRegistrationSelector();
    const [
        dTagLoginSelfExclusionArticleId = DEFAULT_SELF_EXCLUSION_LOGIN_ARTICLE,
        dTagLoginRetailSelfExclusionArticleId = DEFAULT_RETAIL_SELF_EXCLUSION_LOGIN_ARTICLE,
        dTagLoginTimeOutArticleId = DEFAULT_TIME_OUT_LOGIN_ARTICLE,
        dTagLoginAccountFrozenArticleId = DEFAULT_ACCOUNT_FROZEN_ARTICLE_AFTER_LOGIN,
        dTagRegistrationAccountFrozenArticleId = DEFAULT_ACCOUNT_FROZEN_ARTICLE_AFTER_REGISTRATION,
        dTagAllowedNumberOfFailedLoginAttempts = '0',
        dTagForgottenPasswordURL = '/forgotten-password'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.loginSelfExclusionArticleId,
        DYNAMIC_TAG_NAMES.loginRetailSelfExclusionArticleId,
        DYNAMIC_TAG_NAMES.loginTimeOutArticleId,
        DYNAMIC_TAG_NAMES.loginAccountFrozenArticleId,
        DYNAMIC_TAG_NAMES.registrationAccountFrozenArticleId,
        DYNAMIC_TAG_NAMES.allowedNumberOfFailedLoginAttempts,
        DYNAMIC_TAG_NAMES.forgottenPasswordUrl
    ]);

    const getArticleIdsObj = useCallback((afterRegisterLogin) => {
        const accountFrozenArticleId = afterRegisterLogin ?
            dTagRegistrationAccountFrozenArticleId :
            dTagLoginAccountFrozenArticleId;

        return {
            [SELF_EXCLUSION_LOGIN_CODE]: regClientPlatform === RETAIL_PLATFORM
                ? dTagLoginRetailSelfExclusionArticleId
                : dTagLoginSelfExclusionArticleId,
            [TIME_OUT_LOGIN_CODE]: dTagLoginTimeOutArticleId,
            [ACCOUNT_FROZEN]: accountFrozenArticleId,
            [ACCOUNT_FROZEN_SECOND]: accountFrozenArticleId,
            [ACCOUNT_FROZEN_ERROR_CODE]: accountFrozenArticleId,
            22: dTagLoginAccountFrozenArticleId
        };
    }, [
        regClientPlatform,
        dTagLoginTimeOutArticleId,
        dTagLoginAccountFrozenArticleId,
        dTagLoginSelfExclusionArticleId,
        dTagLoginRetailSelfExclusionArticleId,
        dTagRegistrationAccountFrozenArticleId
    ]);

    const checkFailedLoginAttempts = useCallback(async (userName) => {
        const userNameHash = await getSha256(userName);
        const storageData = storageService.local.getItem(FAILED_LOGIN_ATTEMPTS) || {};
        const numberOfFailedLogin = (storageData[userNameHash] || 0) + 1;

        if (numberOfFailedLogin >= Number(dTagAllowedNumberOfFailedLoginAttempts)) {
            navigate(dTagForgottenPasswordURL, { isRedirectFromLoginPopup: true });
            closeLoginPopup();

            return;
        }

        storageService.local.setItem(FAILED_LOGIN_ATTEMPTS, {
            ...storageData,
            [userNameHash]: numberOfFailedLogin
        });
    }, [closeLoginPopup, dTagAllowedNumberOfFailedLoginAttempts, dTagForgottenPasswordURL, navigate]);

    return useCallback(({ response, onClose, ignorePopupForErrorCodes = [], userName, afterRegisterLogin } = {}) => {
        const articleIdsObj = getArticleIdsObj(afterRegisterLogin);
        const message = path(['data', 'actions', 'PlayerActionShowMessage', 0], response) ||
            path(['data', 'userErrors', 'UserError', 0], response) ||
            {};
        const { data } = response.response || response;
        const { errorCode, errorText, isPrevented } = data;
        const playerMessage = response.data?.playerMessage || response?.data?.errorMessage || message;
        const errorKey = message?.templateId || errorText || errorCode;
        const articleId = ignorePopupForErrorCodes.includes(errorKey) ? false : articleIdsObj[errorKey];

        if (htcmdService.isEnabled && isPrevented) {
            return;
        }

        dispatcher.dispatch('auth:login:failed', {
            errorCode,
            playerMessage,
            errorText: errorText || 'unknown'
        });

        if (articleId) {
            loginExtendedErrorPopup({
                messageToParse: message?.message || '',
                articleId,
                title: translate('extended.title', [userName]),
                onClose,
                onlyArticle: ONLY_ARTICLES_CONTENT.includes(errorKey),
                withLogo: WITH_LOGO_POPUPS.includes(errorKey)
            });

            closeLoginPopup();
            dispatcher.dispatch('auth:login:failed:withPopup');

            return;
        }

        if (response?.data?.errorCode === DOMINANT_CARD_ERROR_CODE && response?.data?.username?.username) { // Handling dominant card auto set into username
            storeRememberMeItem(response.data.username.username);
        }

        if (response?.data?.errorCode === RECAPTCHA_ERROR_CODE) {
            return response;
        }

        if (errorCode === INCORRECT_USERNAME_OR_PASSWORD_ERROR_CODE && Number(dTagAllowedNumberOfFailedLoginAttempts)) {
            checkFailedLoginAttempts(userName);
        }

        return {
            [FORM_ERROR]: translate(`error.${response?.data?.errorCode}`, translate('error'))
        };
    }, [
        getArticleIdsObj,
        checkFailedLoginAttempts,
        dTagAllowedNumberOfFailedLoginAttempts,
        translate,
        loginExtendedErrorPopup,
        closeLoginPopup
    ]);
};
